import React, {
  Dispatch,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import Form from 'react-bootstrap/Form'
import { useTranslation } from 'react-i18next'

import * as constants from '../../utils'

import TextInput from './TextInput'

type KeyUploadProps = {
  entityData: { keyName: string; aadRevocation: string }
  setEntityData: Dispatch<
    React.SetStateAction<{ keyName: string; aadRevocation: string }>
  >
  uploadData: any
  setUploadData: Dispatch<React.SetStateAction<any>>
  setFormReady: Dispatch<React.SetStateAction<boolean>>
}

const KeyUpload = forwardRef(
  (
    {
      uploadData,
      setUploadData,
      setFormReady,
      entityData,
      setEntityData,
    }: KeyUploadProps,
    ref,
  ) => {
    const { t } = useTranslation()
    const [errors, setErrors]: any = useState({})
    const [showErrors, setShowErrors]: any = useState({})
    const isFormCompleted =
      entityData.keyName !== '' && entityData.aadRevocation !== ''
    useEffect(() => {
      setFormReady(false)
    }, [])

    const [fileLabel, setFileLabel] = useState(t('key.choose_pfx_file'))
    const [status, setStatus]: any = useState('')
    const [isFileValid, setIsFileValid] = useState(false)

    const isFormValid = () => {
      const keyName = entityData.keyName.trim()
      if (keyName === '') errors.keyName = t('key_error.key_name')
      else if (keyName.length > constants.maxKeyNameLength)
        errors.keyName = t('key_error.key_name_length')
      else errors.keyName = ''

      if (entityData.aadRevocation.trim() === '')
        errors.aadRevocation = t('key_error.aad_revocation')
      else errors.aadRevocation = ''

      setErrors({ ...errors })
      return Object.keys(errors).filter((i) => errors[i] !== '').length == 0
    }

    const validateForm = () => {
      if (isFormValid() && isFormCompleted && isFileValid) setFormReady(true)
      else setFormReady(false)
    }

    const isEmptyObject = (obj): boolean => {
      let name
      for (name in obj) {
        return false
      }
      return true
    }

    useImperativeHandle(ref, () => ({
      validate() {
        if (isEmptyObject(uploadData)) {
          setStatus(
            <p className="responsive-font text-danger font-weight-bold">
              {t('key.upload_valid_pfx')}
            </p>,
          )
        }
        isFormValid()
        setShowErrors(Object.assign({}, errors))
      },
    }))

    const handleChange = (event) => {
      const key = event.target && event.target.name
      const value = event.target && event.target.value
      setEntityData({ ...entityData, [key]: value })
    }

    const handleBrowse = (event) => {
      const file = event.target.files[0]
      setFileLabel(file.name)
      setUploadData(file)
      validateForm()
      if (event.target.files != null) {
        const ext: any = /^.+\.([^.]+)$/.exec(event.target.files[0].name) // getting file extension using regex

        if (ext[1] !== 'pfx') {
          setFormReady(false)
          setIsFileValid(false)
          setStatus(
            <p className="responsive-font text-danger font-weight-bold">
              {t('key.upload_valid_pfx')}
            </p>,
          )
        } else {
          setStatus()
          setIsFileValid(true)
          if (isFormCompleted) setFormReady(true)
        }
      }
    }

    return (
      <>
        <Form.Group controlId="formFile" className="mb-3">
          <Form.Control
            title={fileLabel}
            accept=".pfx"
            onChange={handleBrowse}
            type="file"
            className="responsive-font"
          />
        </Form.Group>
        <div className="mt-4 text-center responsive-font">{status}</div>
        <Form.Group controlId="formFile" className="mb-3">
          <TextInput
            name="keyName"
            label={t('key.name')}
            value={entityData.keyName}
            onKeyUp={validateForm}
            onChange={handleChange}
            error={showErrors.keyName}
          />
        </Form.Group>
        <Form.Group controlId="formFile" className="mb-3">
          <TextInput
            name="aadRevocation"
            label={t('key.aad_revocation')}
            value={entityData.aadRevocation}
            onKeyUp={validateForm}
            onChange={handleChange}
            error={showErrors.aadRevocation}
          />
        </Form.Group>
      </>
    )
  },
)

export default KeyUpload
